import { Component, OnInit, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { ApiService } from '@core/services/api.service';
import { AppConstants } from '@core/constants';
import { SessionService } from '@core/services/session.service';
import { ITileConfig } from '@shared/components/interfaces/tile.interface';
import { OnboardingService } from '@shared/services/onboarding/onboarding.service';

interface IOnboardingMedia {
  lactate: unknown;
  ppd: unknown;
}

interface ISagaConfig {
  guid: string;
  current_step: string;
  saga_data: ISagaDataItem[];
}

interface ISagaDataItem {
  [key: string]: unknown;
}

interface IUseCaseItem {
  tags: string[];
  buttonName: string;
  is_onboarding_track: boolean;
  [key: string]: any;
}

@Component({
  selector: 'app-onboarding-process',
  templateUrl: './onboarding-process.component.html',
  styleUrls: ['./onboarding-process.component.scss'],
})
export class OnboardingProcessComponent implements OnInit {
  private readonly sessionService: SessionService = inject(SessionService);
  private readonly onboardingService: OnboardingService = inject(OnboardingService);
  private readonly apiService: ApiService = inject(ApiService);
  private readonly snackBar: MatSnackBar = inject(MatSnackBar);
  private readonly activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private router = inject(Router);

  public currentStep: number = 1;
  public tiles: ITileConfig[] = [];
  public lactateMedia: unknown;
  public ppdMedia: unknown;

  ngOnInit(): void {
    this.initializeQueryParams();
    this.initializeOnboardingData();
  }

  private initializeQueryParams(): void {
    this.activatedRoute.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params: Params): void => {
      const pageIndex: number = Number(params['page_index_to_return']);
      if (pageIndex) {
        this.currentStep = pageIndex;
        this.loadTiles();
      }
    });
  }

  private initializeOnboardingData(): void {
    this.apiService
      .get(AppConstants.API.ACCOUNTS.ONBOARDING.MEDIA)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (mediaData: IOnboardingMedia): void => {
          this.setMediaData(mediaData);
          this.updateOnboardingStep();
        },
        error: (error: unknown): void => {
          this.showErrorMessage('Failed to load onboarding media');
        },
      });
  }

  private setMediaData(mediaData: IOnboardingMedia): void {
    this.lactateMedia = mediaData.lactate;
    this.ppdMedia = mediaData.ppd;
  }

  private updateOnboardingStep(): void {
    const sagaConfig: ISagaConfig = this.onboardingService.getLocalStorageSagaConfig();
    const lastSagaElement: ISagaDataItem | undefined = sagaConfig.saga_data[sagaConfig.saga_data.length - 1];

    if (!lastSagaElement) {
      return;
    }

    if (sagaConfig.saga_data.length > 1) {
      this.currentStep = 2;
    }

    const isCompleted: boolean = sagaConfig.current_step === 'completed';
    this.onboardingService.setOnboardingStatus(isCompleted);
  }

  public onGetStarted(): void {
    const stepName: string = 'started';
    const sagaConfig: ISagaConfig = this.onboardingService.getLocalStorageSagaConfig();

    if (!sagaConfig.guid) {
      this.showErrorMessage('Onboarding error, GUID is missing');
      return;
    }

    this.currentStep = 2;

    const hasStep: boolean = this.onboardingService.hasSagaDataStep(stepName);
    if (!hasStep) {
      this.onboardingService.setStepForSaga(stepName, 60);
    }
  }

  public onExploreUseCases(): void {
    this.router.navigate(['/home/onboarding/use-cases']);
    this.loadTiles();
  }

  public onExploreUseCases2(): void {
    this.router.navigate(['/home/onboarding/use-cases-2']);
    this.loadTiles();
  }

  public onProceedWithLactate(): void {
    const stepName: string = 'lactate-popup-video';
    this.processStepAndOpenModal(stepName, 'lactate', this.lactateMedia);
  }

  public onProceedWithPPD(): void {
    const stepName: string = 'ppd-popup-video';
    this.processStepAndOpenModal(stepName, 'ppd', this.ppdMedia);
  }

  public onBackToPrevious(): void {
    this.currentStep = 2;
  }

  private processStepAndOpenModal(stepName: string, modalType: string, media: unknown): void {
    const hasStep: boolean = this.onboardingService.hasSagaDataStep(stepName);

    if (!hasStep) {
      const currentPercentage: number = this.onboardingService.getCurrentPercentage();
      this.onboardingService.setStepForSaga(stepName, currentPercentage);
    }

    this.onboardingService.openStepperModal(modalType, media);
  }

  private loadTiles(): void {
    this.onboardingService
      .getUseCases()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: IUseCaseItem[]): void => {
          console.log('Use Cases: ', response);

          this.tiles = this.mapUseCasesToTiles(response);
        },
        error: (error: unknown): void => {
          this.showErrorMessage('Failed to load use cases');
        },
      });
  }

  private mapUseCasesToTiles(useCaseList: IUseCaseItem[]): ITileConfig[] {
    return useCaseList.map(
      (item: IUseCaseItem): ITileConfig =>
        ({
          ...item,
          tags: [''],
          buttonName: 'Use This Case',
          is_onboarding_track: true,
        }) as ITileConfig
    );
  }

  private showErrorMessage(message: string): void {
    this.snackBar.open(message, 'OK', AppConstants.TOAST_CONFIG.ERROR);
  }
}
